import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Users, Map as MapIcon } from "lucide-react";
import { mh, useMhStream } from "@/lib/mh";
import { money } from "@/lib/onb";
import { Eyebrow, Panel } from "@/components/mh/bits";

const STATUS = { active: ["Active", "#D4FF3A"], idle: ["In dev", "#FFB020"], offline: ["Not started", "#5A5A66"] };
const Pill = ({ s }) => <span className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5" style={{ color: STATUS[s][1] }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS[s][1] }} />{STATUS[s][0]}</span>;

const RoleRow = ({ r }) => (
  <Link to={`/atlas/${r.slug}`} data-testid={`fleet-role-${r.slug}`} className="flex items-center gap-3 py-3 pl-12 pr-4 border-l-2 ml-6 hover:bg-white/[0.03] transition-colors" style={{ borderColor: `${r.hex}44` }}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 flex-wrap"><span className="text-sm font-medium">{r.name}</span><Pill s={r.status} /></div>
      <div className="flex flex-wrap gap-x-3 mt-1 font-mono text-[10px] text-muted-foreground">
        <span>{r.stage}</span><span>{r.autonomy}</span>{r.wave && <span>Wave {r.wave}</span>}<span>Runs today: {r.runs_today}</span>
        {r.success_rate != null && <span>Success: {r.success_rate}%</span>}{r.owner_email && <span>Owner: {r.owner_email}</span>}
      </div>
    </div>
    {r.value && <span className="font-mono text-xs text-[#D4FF3A] hidden sm:block">${money(r.value.money_low)}–{money(r.value.money_high)}/mo</span>}
  </Link>
);

const LeadRow = ({ l, open, toggle }) => (
  <div data-testid={`fleet-lead-${l.slug}`}>
    <button onClick={toggle} data-testid={`fleet-toggle-${l.slug}`} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.03] transition-colors">
      {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      <span className="w-9 h-9 rounded-full border flex items-center justify-center font-mono text-[10px]" style={{ borderColor: l.hex, color: l.hex, boxShadow: `0 0 18px ${l.hex}33` }}>{l.children.length}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap"><span className="font-display text-2xl" style={{ color: l.hex }}>{l.name}</span><Pill s={l.status} /></div>
        <div className="font-mono text-[10px] text-muted-foreground">{l.tagline} · {l.children.length} agents · {l.live} live · {l.runs_today} runs today</div>
      </div>
      {l.money_high > 0 && <span className="font-mono text-xs text-[#D4FF3A] hidden sm:block">${money(l.money_low)}–{money(l.money_high)}/mo</span>}
    </button>
    {open && <div className="pb-2">{l.children.map((r) => <RoleRow key={r.slug} r={r} />)}</div>}
  </div>
);

export default function Agents() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => mh.fleet().then((r) => setData(r.data)).catch(() => setData({ leads: [], total: 0 })), []);
  useEffect(() => { load(); }, [load]);
  useMhStream((ev) => { if (["tailor.progress", "tailor.role", "agent.status"].includes(ev.type)) load(); });
  const leads = (data?.leads || []).map((l) => ({ ...l, children: l.children.filter((c) => filter === "all" || c.status === filter) })).filter((l) => l.children.length);
  const all = (data?.leads || []).flatMap((l) => l.children);
  return (
    <div className="max-w-[1300px] mx-auto space-y-6" data-testid="agents-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mh-rise">
        <div>
          <Eyebrow><span data-testid="fleet-summary">{data?.tailored ? "From your tailored map" : "Live and in-development roles"} · {all.length} agents · synced live</span></Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">The fleet, <em className="text-muted-foreground">straight from your map.</em></h1>
        </div>
        <div className="flex gap-1.5" data-testid="fleet-filters">
          {["all", "active", "idle", "offline"].map((k) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`fleet-filter-${k}`}
              className={`h-8 px-3 rounded-full border text-[11px] transition-colors ${filter === k ? "border-[#D4FF3A]/60 text-[#D4FF3A] bg-[#D4FF3A]/10" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
              {k === "all" ? "All" : STATUS[k][0]} {k === "all" ? all.length : all.filter((a) => a.status === k).length}
            </button>
          ))}
        </div>
      </header>
      <Panel cls="divide-y divide-white/5" testId="fleet-list">
        {!data && <div className="p-8 text-sm text-muted-foreground">Loading fleet…</div>}
        {leads.map((l) => <LeadRow key={l.slug} l={l} open={!!open[l.slug]} toggle={() => setOpen((o) => ({ ...o, [l.slug]: !o[l.slug] }))} />)}
        {data && !leads.length && (
          <div className="text-center py-14 text-muted-foreground" data-testid="fleet-empty">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No active roles yet. Switch roles on in your map and they appear here.</p>
            <Link to="/build" className="inline-flex items-center gap-2 mt-4 text-xs underline"><MapIcon className="w-3.5 h-3.5" />Build your map</Link>
          </div>
        )}
      </Panel>
    </div>
  );
}
